import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { fiscalDeviceScopeSchema, zReportSourceSchema } from "./enums.js";
/**
 * ÖKC (yeni nesil yazar kasa) cihazları ve Z raporları (Prompt 13 — API C aşaması).
 *
 * Değişmezler (ADR 0013 karar 13):
 *  · `fiscal_devices` ASGARİ cihaz kaydıdır: "hangi cihazdan Z beklenir ve hangi satışları kapsar". Tam ÖKC
 *    bütünleşmesi Prompt 14–15'tedir.
 *  · Karşılaştırma CİHAZ BAZINDA DEĞİL, KAPSAM (`FUEL` / `MARKET` / `ALL`) toplamı bazındadır: bir istasyonda
 *    aynı kapsamda birden çok ÖKC varsa Σ Z ↔ sistem karşılaştırılır (`comparedAsGroup: true`).
 *  · Fark eşiği ayardır (`zMismatchThreshold`); aşılırsa `mismatch` + `shifts.z_report.mismatch` alarm olayı.
 *  · Gün henüz tamamlanmadıysa (hesaplar kabul edilmemiş) sonuç `provisional`'dır ve gün sonunda tazelenir.
 */
export const zVatBreakdownLineSchema = z.object({
    ratePercent: rateSchema.describe("KDV yüzdesi (ör. 20)"),
    gross: nonNegativeMoneySchema.describe("KDV DÂHİL tutar (kuruş)"),
    vat: nonNegativeMoneySchema.describe("KDV tutarı (kuruş)"),
});
/** Z raporundaki ödeme türü kırılımı. Anahtarlar cihazdan gelir (CASH / CARD / OTHER …). */
export const zPaymentBreakdownSchema = z
    .record(z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Ödeme türü anahtarı büyük harf olmalıdır"), nonNegativeMoneySchema)
    .describe("Ödeme türü → kuruş");
// ── ÖKC cihazı ───────────────────────────────────────────────────────────────
export const fiscalDeviceSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    serialNo: z.string().describe("ÖKC sicil / seri no"),
    label: z.string(),
    scope: fiscalDeviceScopeSchema,
    isActive: z.boolean(),
    /**
     * Hizmete giriş günü (dâhil). Doluysa cihaz bu günden ÖNCEKİ iş günlerinde "Z beklenen cihaz" SAYILMAZ:
     * sonradan takılan ÖKC yüzünden geçmiş günlerin gün sonu kontrol listesi yanlış eksik bildirmez (Prompt 14).
     */
    inServiceFrom: localDateSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int().min(1),
});
export const fiscalDeviceListSchema = z.object({ items: z.array(fiscalDeviceSchema) });
export const listFiscalDevicesQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    activeOnly: z.coerce.boolean().optional(),
});
export const createFiscalDeviceSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    serialNo: z.string().trim().min(1).max(40),
    label: z.string().trim().min(1).max(80),
    scope: fiscalDeviceScopeSchema.default("FUEL"),
    inServiceFrom: localDateSchema.nullable().optional().describe("Hizmete giriş günü (dâhil)"),
});
/** Kısmi güncelleme (PATCH): yalnızca verilen alanlar değişir. İstasyon ve seri no DEĞİŞMEZ. */
export const patchFiscalDeviceSchema = z.object({
    label: z.string().trim().min(1).max(80).optional(),
    scope: fiscalDeviceScopeSchema.optional(),
    isActive: z.boolean().optional(),
    inServiceFrom: localDateSchema.nullable().optional(),
    version: z.number().int().min(1),
});
export const fiscalDeviceParamsSchema = z.object({ id: uuidSchema });
// ── Z raporu ─────────────────────────────────────────────────────────────────
export const createZReportSchema = z.object({
    fiscalDeviceId: uuidSchema,
    zNo: z.number().int().min(1).max(1_000_000),
    businessDate: localDateSchema,
    reportAt: timestampSchema,
    totalAmount: nonNegativeMoneySchema.describe("KDV dâhil gün toplamı (kuruş)"),
    vatBreakdown: z.array(zVatBreakdownLineSchema).max(20).default([]),
    paymentBreakdown: zPaymentBreakdownSchema.default({}),
    receiptCount: z.number().int().min(0).max(1_000_000).nullable().optional(),
    source: zReportSourceSchema.default("MANUAL"),
    fileId: uuidSchema.nullable().optional().describe("Rapor görseli (yüklenmiş dosya)"),
});
export const resolveZReportSchema = z.object({
    note: z.string().trim().min(3).max(1000).describe("Farkın neden kabul edildiği"),
});
export const zReportSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    fiscalDeviceId: uuidSchema,
    fiscalDeviceSerialNo: z.string(),
    fiscalDeviceLabel: z.string(),
    scope: fiscalDeviceScopeSchema,
    zNo: z.number().int(),
    businessDate: localDateSchema,
    reportAt: timestampSchema,
    totalAmount: moneySchema,
    vatBreakdown: z.array(zVatBreakdownLineSchema),
    paymentBreakdown: zPaymentBreakdownSchema,
    receiptCount: z.number().int().nullable(),
    source: zReportSourceSchema,
    /** Kapsam toplamı bazında sistem tutarı (kuruş); hiç hesaplanmadıysa null. */
    systemAmount: moneySchema.nullable(),
    /** Z − sistem (işaretli). */
    difference: moneySchema.nullable(),
    mismatch: z.boolean(),
    /** true → aynı kapsamda birden çok cihaz var; karşılaştırma Σ Z ↔ sistem üzerinden yapıldı. */
    comparedAsGroup: z.boolean(),
    /**
     * true → günün hesapları henüz tamamlanmamıştı; karşılaştırma GEÇİCİDİR ve gün sonu kontrol listesi
     * hesaplanırken / gün kapatılırken yeniden yapılır.
     */
    provisional: z.boolean(),
    resolvedAt: timestampSchema.nullable(),
    resolvedBy: uuidSchema.nullable(),
    resolutionNote: z.string().nullable(),
    fileId: uuidSchema.nullable(),
    businessDayLocked: z.boolean(),
    createdAt: timestampSchema,
    version: z.number().int().min(1),
});
export const zReportPageSchema = pageOf(zReportSchema);
export const listZReportsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    fiscalDeviceId: uuidSchema.optional(),
    from: localDateSchema.optional().describe("İş günü (dâhil)"),
    to: localDateSchema.optional().describe("İş günü (dâhil)"),
    mismatchOnly: z.coerce.boolean().optional().describe("Yalnızca çözülmemiş farklı raporlar"),
});
export const zReportParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=z-reports.js.map